import type { ComputedRef } from 'vue';
import { computed, ref } from 'vue';
import { preciseValue } from '@/filters';
import { DEFAULT_NOTIONAL } from '@/utils/portfolioDefaults';

const internalNotional = ref(DEFAULT_NOTIONAL);

/**
 * Notional Parameters confirmed for this Factor Composition visit.
 * Null means the visit scales by the draft portfolio value.
 */
const confirmedReportingNotional = ref<number | null>(null);

const reportingNotional = computed(() => {
  return internalNotional.value;
});

const parseReportingNotional = (val: string): number | null => {
  const validVal = parseFloat(val);
  if (isNaN(validVal) || validVal < 0) return null;

  return validVal;
};

const updateParameters = (val: string): void => {
  const validVal = parseReportingNotional(val);
  if (validVal == null) return;

  internalNotional.value = validVal;
};

/**
 * Applies an FX rate to the strategy reporting notional, and to the confirmed Factor Composition
 * notional when one exists so the confirmed scale converts with the display currency.
 */
const scaleReportingNotional = (fxRate: number): void => {
  internalNotional.value = preciseValue(internalNotional.value * fxRate);

  if (confirmedReportingNotional.value != null) {
    confirmedReportingNotional.value = preciseValue(confirmedReportingNotional.value * fxRate);
  }
};

const clearConfirmedReportingNotional = (): void => {
  confirmedReportingNotional.value = null;
};

const toDefault = (): void => {
  clearConfirmedReportingNotional();
  internalNotional.value = DEFAULT_NOTIONAL;
};

export default function (): {
  reportingNotional: ComputedRef<number>;
  confirmedReportingNotional: ComputedRef<number | null>;
  updateParameters: (units: string) => void;
  scaleReportingNotional: (fxRate: number) => void;
  clearConfirmedReportingNotional: () => void;
  toDefault: () => void;
} {
  return {
    reportingNotional,
    confirmedReportingNotional: computed((): number | null => confirmedReportingNotional.value),
    updateParameters,
    scaleReportingNotional,
    clearConfirmedReportingNotional,
    toDefault,
  };
}
